import type { Workday } from '../api/types';

export type WorkdayStatus = 'NOT_STARTED' | 'IN_WORK' | 'IN_BREAK' | 'ENDED';

export type WorkdayDurations = {
  workMin: number;
  breakMin: number;
  netMin: number;
};

export type WorkSegmentKind = 'WORK' | 'BREAK';

export type WorkSegment = {
  id: string;
  workdayId: string;
  userId: string;
  kind: WorkSegmentKind;
  start: Date;
  end: Date;
  label: string;
  workday: Workday;
};

export function computeStatus(w: Workday): WorkdayStatus {
  if (!w.start_work_at) return 'NOT_STARTED';
  if (w.end_work_at) return 'ENDED';
  if (w.start_break_at && !w.end_break_at) return 'IN_BREAK';
  return 'IN_WORK';
}

function parseISO(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d : null;
}

function diffMs(a: Date, b: Date) {
  return b.getTime() - a.getTime();
}

function clampDate(d: Date, min: Date, max: Date) {
  if (d.getTime() < min.getTime()) return min;
  if (d.getTime() > max.getTime()) return max;
  return d;
}

export function computeDurationsClient(w: Workday, now: Date = new Date()): WorkdayDurations {
  const sw = parseISO(w.start_work_at);
  if (!sw) return { workMin: 0, breakMin: 0, netMin: 0 };

  const ew = parseISO(w.end_work_at) || now;

  const sb = parseISO(w.start_break_at);
  const eb = parseISO(w.end_break_at) || (sb ? now : null);

  const workMs = Math.max(0, diffMs(sw, ew));
  const breakMs = (sb && eb) ? Math.max(0, diffMs(sb, eb)) : 0;

  const workMin = Math.floor(workMs / 60000);
  const breakMin = Math.floor(breakMs / 60000);
  const netMin = Math.max(0, workMin - breakMin);

  return { workMin, breakMin, netMin };
}

/**
 * Segmenti per rappresentazione tipo calendario.
 *
 * - WORK: lavoro effettivo (spezzato in 2 se c'è una pausa)
 * - BREAK: pausa
 */
export function buildSegments(w: Workday, now: Date = new Date()): WorkSegment[] {
  const sw = parseISO(w.start_work_at);
  if (!sw) return [];

  const ew = parseISO(w.end_work_at) || now;
  const sb = parseISO(w.start_break_at);
  const eb = parseISO(w.end_break_at) || (sb ? now : null);

  const segments: WorkSegment[] = [];

  // Work before break (or full day if no break)
  if (!sb) {
    segments.push({
      id: `${w._id}:work:1`,
      workdayId: w._id,
      userId: w.user_id,
      kind: 'WORK',
      start: sw,
      end: ew,
      label: 'Lavoro',
      workday: w,
    });
    return segments;
  }

  const work1End = clampDate(sb, sw, ew);
  if (work1End.getTime() > sw.getTime()) {
    segments.push({
      id: `${w._id}:work:1`,
      workdayId: w._id,
      userId: w.user_id,
      kind: 'WORK',
      start: sw,
      end: work1End,
      label: 'Lavoro',
      workday: w,
    });
  }

  // Break
  if (sb && eb) {
    const bStart = clampDate(sb, sw, ew);
    const bEnd = clampDate(eb, sw, ew);
    if (bEnd.getTime() > bStart.getTime()) {
      segments.push({
        id: `${w._id}:break:1`,
        workdayId: w._id,
        userId: w.user_id,
        kind: 'BREAK',
        start: bStart,
        end: bEnd,
        label: 'Pausa',
        workday: w,
      });
    }
  }

  // Work after break
  if (eb) {
    const w2Start = clampDate(eb, sw, ew);
    if (ew.getTime() > w2Start.getTime()) {
      segments.push({
        id: `${w._id}:work:2`,
        workdayId: w._id,
        userId: w.user_id,
        kind: 'WORK',
        start: w2Start,
        end: ew,
        label: 'Lavoro',
        workday: w,
      });
    }
  }

  return segments;
}
